import { useRef, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";
import { isBitSet } from "../../utils";

const SHIFT_COLORS = [
    "#2563EB",
    "#22C55E",
    "#F59E0B",
    "#EF4444",
    "#A855F7",
    "#0EA5E9",
    "#14B8A6",
    "#E11D48",
    "#F97316",
    "#84CC16",
    "#06B6D4",
    "#D946EF",
];

function hashId(id) {
    if (typeof id === "number") return id;
    return String(id)
        .split("")
        .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
}
function pickBgColor(id) {
    return SHIFT_COLORS[hashId(id) % SHIFT_COLORS.length];
}
function contrastText(hex) {
    const h = hex.replace("#", "");
    const r = parseInt(h.length === 3 ? h[0] + h[0] : h.slice(0, 2), 16);
    const g = parseInt(h.length === 3 ? h[1] + h[1] : h.slice(2, 4), 16);
    const b = parseInt(h.length === 3 ? h[2] + h[2] : h.slice(4, 6), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? "#000000" : "#FFFFFF";
}

function makeLocalDate(baseDay, sec) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return new Date(
        baseDay.getFullYear(),
        baseDay.getMonth(),
        baseDay.getDate(),
        h,
        m,
        0,
        0,
    );
}
function endOfDayLocal(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 0);
}
function addDaysLocal(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
}

const generateEvents = (dateRange, shifts) => {
    if (!dateRange || !dateRange.start || !dateRange.end || !shifts?.length)
        return [];

    const events = [];
    const oneDay = 24 * 60 * 60 * 1000;

    for (const s of shifts) {
        const {
            id,
            name,
            start_time: st = 0,
            end_time: et = 0,
            runs_on,
            created_at,
        } = s;
        const bg = pickBgColor(id);
        const fg = contrastText(bg);
        const shiftCreatedAt = created_at ? new Date(created_at) : new Date(0);
        shiftCreatedAt.setHours(0, 0, 0, 0);
        // Start checking 1 day before the view starts, so we catch shifts that
        // began yesterday and cross midnight into the current view.
        let current = addDaysLocal(new Date(dateRange.start), -1);
        while (current < dateRange.end) {
            if (current >= shiftCreatedAt) {
                const dayIndex = current.getDay();

                if (isBitSet(runs_on, dayIndex)) {
                    const dayStart = new Date(current);
                    const crossesMidnight = st >= et;

                    if (!crossesMidnight) {
                        events.push({
                            id: `${id}-${dayStart.getTime()}-single`,
                            title: name || "Shift",
                            start: makeLocalDate(dayStart, st),
                            end: makeLocalDate(dayStart, et),
                            extendedProps: { shift: s },
                            color: bg,
                            textColor: fg,
                        });
                    } else {
                        events.push({
                            id: `${id}-${dayStart.getTime()}-A`,
                            title: name || "Shift",
                            start: makeLocalDate(dayStart, st),
                            end: endOfDayLocal(dayStart),
                            extendedProps: { shift: s, overnight: true },
                            color: bg,
                            textColor: fg,
                        });
                        const nextDay = addDaysLocal(dayStart, 1);
                        events.push({
                            id: `${id}-${dayStart.getTime()}-B`,
                            title: name,
                            start: makeLocalDate(nextDay, 0),
                            end: makeLocalDate(nextDay, et),
                            extendedProps: { shift: s, overnight: true },
                            color: bg,
                            textColor: fg,
                        });
                    }
                }
            }
            current = new Date(current.getTime() + oneDay);
        }
    }
    return events;
};

const ShiftWeekCalendar = ({ shifts, onEventClick }) => {
    const calRef = useRef(null);

    useEffect(() => {
        const api = calRef.current?.getApi();
        if (api) {
            api.refetchEvents();
        }
    }, [shifts]);

    return (
        <div className="tw-rounded-lg tw-border tw-bg-white tw-shadow-sm">
            <div className="tw-p-4">
                <FullCalendar
                    ref={calRef}
                    plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin]}
                    initialView="timeGridWeek"
                    headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "dayGridMonth,timeGridWeek,timeGridDay",
                    }}
                    timeZone="local"
                    allDaySlot={false}
                    slotMinTime="00:00:00"
                    slotMaxTime="24:00:00"
                    height="auto"
                    events={(info, successCallback) => {
                        const events = generateEvents(
                            { start: info.start, end: info.end },
                            shifts,
                        );
                        successCallback(events);
                    }}
                    eventClick={(info) =>
                        onEventClick?.(info.event.extendedProps.shift)
                    }
                    eventTimeFormat={{
                        hour: "2-digit",
                        minute: "2-digit",
                        meridiem: "short",
                    }}
                    nowIndicator
                    eventDisplay="block"
                    eventDidMount={(info) => {
                        const bg = info.event.backgroundColor || "#E5E7EB";
                        const border =
                            info.event.borderColor ||
                            info.event.backgroundColor ||
                            "#374151";
                        Object.assign(info.el.style, {
                            backgroundColor: `${bg}20`,
                            border: `2px solid ${border}`,
                            borderRadius: "8px",
                            color: border,
                            fontWeight: "500",
                        });
                    }}
                />
            </div>
        </div>
    );
};

export default ShiftWeekCalendar;
